import type { ReactNode } from 'react';

export function Card({ title, action, children, tight }: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  tight?: boolean;
}) {
  return (
    <section className="card">
      {(title || action) && (
        <header className="card-head">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {action}
        </header>
      )}
      <div className={tight ? 'card-body tight' : 'card-body'}>{children}</div>
    </section>
  );
}

export function Stat({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return (
    <div className="card stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {note && <div className="note">{note}</div>}
    </div>
  );
}

const TONE: Record<string, string> = {
  active: 'good', paid: 'good', clean: 'good', published: 'good', countersigned: 'good',
  signed: 'good', won: 'good', complete: 'good', qualified: 'accent',
  onboarding: 'accent', agreement: 'accent', proposal: 'accent', sent: 'accent',
  new: 'accent', open: 'accent', ready: 'accent', trialing: 'accent',
  lead: '', draft: '', internal: '', contacted: '', pending: 'warn',
  paused: 'warn', past_due: 'warn', overdue: 'bad', viewed: 'warn', expired: 'warn',
  churned: 'bad', void: 'bad', canceled: 'bad', infected: 'bad', lost: 'bad', failed: 'bad',
};

export function Chip({ value, tone }: { value: string; tone?: 'good' | 'warn' | 'bad' | 'accent' }) {
  const resolved = tone ?? TONE[value] ?? '';
  return <span className={`chip ${resolved}`}>{value.replace(/_/g, ' ')}</span>;
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <strong>{title}</strong>
      {children}
    </div>
  );
}

export function Notice({ kind = 'info', children }: { kind?: 'info' | 'error' | 'ok' | 'warn'; children: ReactNode }) {
  return <div className={`notice ${kind}`}>{children}</div>;
}

export function PageHead({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function Progress({ complete, total }: { complete: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((complete / total) * 100);
  return (
    <>
      <div className="progress"><div style={{ width: `${pct}%` }} /></div>
      <div className="small muted" style={{ marginTop: 6 }}>{complete} of {total} complete</div>
    </>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

export function Loading() {
  return <div className="empty">Loading…</div>;
}
